/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902182426 — SnackScreen */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { usePageTracking } from '../../../../shared/hooks/usePageTracking';
import { ContentBox, ErrorText, Input, InputWrapper, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { EVENT_NAMES, useSendEventLog } from '../../../event-log';
import { useOrderActions } from '../../../order/hooks/useOrderActions';
import { withOrderHydration } from '../../../order/hooks/withOrderHydration';
import { PaymentOrFeedback } from '../../components/PaymentOrFeedback/PaymentOrFeedback';
import { PriceFromUserInput } from '../../components/PriceDisplay/PriceFromUserInput/PriceFromUserInput';
import { useAmountValidation } from '../../hooks/useAmountValidation';
import { useOrderAmount } from '../../hooks/useOrderAmount';
import { useTimerTimeout } from '../../hooks/useTimerTimeout';
import { AMOUNT_INPUT_DEBOUNCE_MS, LIMITS } from '../../payment.constants';
import { calculateDiscountedAmount, normalizeDecimalInput } from '../../payment.utils';
import styles from './SnackScreen.module.css';

const SnackScreenBody = () => {
    const { t } = useTranslation();
    const order = useAppStore((s) => s.order);
    const vm = useAppStore((s) => s.vm);
    
    const { onTimerTimeoutHandler } = useOrderActions();
    
    const [inputAmount, setInputAmount] = useState<number>(0);
    const [displayAmount, setDisplayAmount] = useState<string>('');
    const [isPaymentButtonVisible, setIsPaymentButtonVisible] = useState<boolean>(false);
    const [error, setError] = useState<string>();
    const { max: maxLimit, validate, limitToMaxAmount } = useAmountValidation({ min: LIMITS.SNACK.MIN });
    const { isAmountSent, submitAmount } = useOrderAmount({ debounceMs: AMOUNT_INPUT_DEBOUNCE_MS });
    const { sendEvent } = useSendEventLog();

    useTimerTimeout({ screenName: 'snack', onTimeout: () => onTimerTimeoutHandler('snack') });

    usePageTracking('snack screen');

    useEffect(() => {
        const realAmount = calculateDiscountedAmount(inputAmount, vm?.campaign?.discount_percentage);
        setIsPaymentButtonVisible(
            inputAmount >= LIMITS.SNACK.MIN && inputAmount <= maxLimit,
        );
        submitAmount(Number.isNaN(realAmount) ? 0 : realAmount);
    }, [inputAmount]);

    const hasAmountEventFiredRef = useRef(false);
    useEffect(() => {
        if (isAmountSent && !hasAmountEventFiredRef.current) {
            sendEvent({ name: EVENT_NAMES.amountSubmitted.snack });
            hasAmountEventFiredRef.current = true;
        }
    }, [isAmountSent]);

    return (
        <ScreenWrapper>
            <ScreenTitle>{t('Enter exact amount')}</ScreenTitle>
            <ContentBox>
                <div className={styles.noteMessage}>{t('Enter exact description')}</div>

                <InputWrapper>
                    <Input
                        type="text"
                        inputMode="decimal"
                        min={LIMITS.SNACK.MIN}
                        max={maxLimit}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const result = normalizeDecimalInput(event.target.value);
                            if (result === null) return;
                            let { display, value: enteredAmount } = result;
                            const validationError = validate(enteredAmount);

                            if (validationError && enteredAmount > maxLimit) {
                                enteredAmount = limitToMaxAmount(enteredAmount);
                                display = String(enteredAmount);
                                setError(validationError);
                                setIsPaymentButtonVisible(false);
                            } else {
                                setError('');
                            }

                            setDisplayAmount(display);
                            setInputAmount(enteredAmount);
                        }}
                        step="1"
                        placeholder={t<string>('Amount')}
                        value={displayAmount}
                    />
                    <ErrorText>{error}</ErrorText>
                </InputWrapper>
                <div className={styles.payText}>{t('Order payment')}</div>

                <PriceFromUserInput
                    amountInSubunits={order?.amount || 0}
                    inputAmount={inputAmount}
                />
                
                <PaymentOrFeedback
                    isVisible={isPaymentButtonVisible}
                    isPending={!isAmountSent}
                    screenName="snack"
                />
            </ContentBox>
        </ScreenWrapper>
    );
};

const SnackScreen = withOrderHydration(SnackScreenBody);

export { SnackScreen };
